import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Badge, Tooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { ScoringConfigType } from "app/pages/sales-intelligence/domain/leads/types/scoring-config";

export type ScoringScopeBadgeKeys = {
    sharedKey: string;
    privateKey: string;
};

type ScoringScopeBadgeProps = {
    scope?: ScoringConfigType;
    scopeBadgeKeys?: ScoringScopeBadgeKeys;
    /** The tooltip states that the rule reaches the whole account, which only holds without teams. */
    showSharedTooltip?: boolean;
};

export const DEFAULT_SCOPE_BADGE_KEYS: ScoringScopeBadgeKeys = {
    sharedKey: "si.playbooks.type.shared",
    privateKey: "si.playbooks.type.private",
};

const SHARED_SCOPE_TOOLTIP_KEY = "si.pages.score_settings.card.scope.shared.tooltip";

export const ScoringScopeBadge = ({
    scope,
    scopeBadgeKeys = DEFAULT_SCOPE_BADGE_KEYS,
    showSharedTooltip = true,
}: ScoringScopeBadgeProps) => {
    const { translate } = useTranslate();

    if (!scope) {
        return null;
    }

    const isShared = scope === ScoringConfigType.Account;

    const badge = (
        <Badge
            bgColor={isShared ? colorsPalettes.carbon["25"] : colorsPalettes.indigo["100"]}
            color={isShared ? colorsPalettes.carbon["400"] : colorsPalettes.indigo["400"]}
            type="custom"
        >
            {translate(isShared ? scopeBadgeKeys.sharedKey : scopeBadgeKeys.privateKey)}
        </Badge>
    );

    if (!isShared || !showSharedTooltip) {
        return badge;
    }

    return (
        <Tooltip
            placement="top"
            content={translate(SHARED_SCOPE_TOOLTIP_KEY)}
        >
            <Flex
                width="fit-content"
                data-automation="scoring-rule-card.scope-badge"
            >
                {badge}
            </Flex>
        </Tooltip>
    );
};
